import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import {
	Form,
	FormGroup,
	Label,
	Input,
	Button,
	FormFeedback,
	Row,
	Col,
} from "reactstrap";
import contasPowerBiService from "services/ContasPowerBiService";
import Loading from "components/Common/Loading";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FloppyDisk } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const FormContasPowerBiConfig = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		name: "",
		username: "",
		password: "",
		client_id: "",
		client_secret: "",
	});
	const [isLoading, setIsLoading] = useState(false);
	const [dataLoading, setDataLoading] = useState(false);
	const [formErrors, setFormErrors] = useState({});

	const fetchData = async () => {
		try {
			const data = await contasPowerBiService.getById(id);
			setFormData({
				name: data.name || "",
				username: data.username || "",
				password: data.password || "",
				client_id: data.client_id || "",
				client_secret: data.client_secret || "",
			});
		} catch (error) {
			console.error("Erro ao buscar dados da conta:", error);
		}
	};

	const getAll = async () => {
		setDataLoading(true);
		try {
			if (id) {
				await fetchData();
			}
		} catch (error) {
			console.error("Erro ao buscar dados:", error);
		} finally {
			setDataLoading(false);
		}
	};

	useEffect(() => {
		getAll();
	}, []);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const validate = () => {
		const errors = {};
		if (!formData.name) errors.name = "Campo obrigatório";
		if (!formData.username) errors.username = "Campo obrigatório";
		if (!formData.password) errors.password = "Campo obrigatório";
		if (!formData.client_id) errors.client_id = "Campo obrigatório";
		if (!formData.client_secret) errors.client_secret = "Campo obrigatório";

		return errors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
		} else {
			setIsLoading(true);
			try {
				if (id) {
					await contasPowerBiService.put(id, formData);
				} else {
					await contasPowerBiService.post(formData);
				}

				setFormErrors({});
				await SendaCustomSwal.fire({
					title: "Sucesso!",
					text: id
						? "Conta editada com sucesso."
						: "Conta criada com sucesso.",
					iconHtml: '<i class="success-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});

				navigate("/relatorios-power-bi-config?tab=contas");
			} catch (error) {
				// Trate o erro conforme necessário
				setFormErrors(error.response?.data || {});
				await SendaCustomSwal.fire({
					title: "Erro",
					text: id
						? "Ocorreu um erro ao tentar editar a conta."
						: "Ocorreu um erro ao tentar criar a conta.",
					iconHtml: '<i class="error-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<Loading isLoading={dataLoading}>
			<div className="page-content">
				<Breadcrumb
					title="Contas Power BI"
					breadcrumbItem="Cadastro de conta"
					backLink="/relatorios-power-bi-config?tab=contas"
				/>

				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<Form onSubmit={handleSubmit}>
						<Row>
							<Col md={6}>
								<FormGroup>
									<Label for="name">
										Nome{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="name"
										id="name"
										value={formData.name}
										onChange={handleInputChange}
										invalid={!!formErrors.name}
										placeholder="Digite o nome da configuração"
									/>
									{formErrors.name && (
										<FormFeedback>
											{formErrors.name}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={6}>
								<FormGroup>
									<Label for="username">
										Usuário{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="username"
										id="username"
										value={formData.username}
										onChange={handleInputChange}
										invalid={!!formErrors.username}
										placeholder="Digite o nome de usuário"
									/>
									{formErrors.username && (
										<FormFeedback>
											{formErrors.username}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md={6}>
								<FormGroup>
									<Label for="password">
										Senha{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="password"
										id="password"
										value={formData.password}
										onChange={handleInputChange}
										invalid={!!formErrors.password}
										placeholder="Digite a senha"
									/>
									{formErrors.password && (
										<FormFeedback>
											{formErrors.password}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={6}>
								<FormGroup>
									<Label for="client_id">
										Client ID{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="client_id"
										id="client_id"
										value={formData.client_id}
										onChange={handleInputChange}
										invalid={!!formErrors.client_id}
										placeholder="Digite o Client ID"
									/>
									{formErrors.client_id && (
										<FormFeedback>
											{formErrors.client_id}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md={12}>
								<FormGroup>
									<Label for="client_secret">
										Client Secret{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="client_secret"
										id="client_secret"
										value={formData.client_secret}
										onChange={handleInputChange}
										invalid={!!formErrors.client_secret}
										placeholder="Digite o Client Secret"
									/>
									{formErrors.client_secret && (
										<FormFeedback>
											{formErrors.client_secret}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<div
								className="page-title-right"
								style={{
									display: "flex",
									justifyContent: "end",
								}}
							>
								<Button
									type="submit"
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										marginTop: 10,
										width: 150,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
									}}
								>
									{isLoading ? (
										<span
											className="spinner-border spinner-border-sm mr-1"
											style={{ marginRight: 10 }}
										></span>
									) : null}
									<FloppyDisk size={16} /> Salvar
								</Button>
							</div>
						</Row>
					</Form>
				</div>
			</div>
		</Loading>
	);
};

export default FormContasPowerBiConfig; 